import { useMemo } from 'react';

import { isDocumentFilled, prismicClient } from '@/data';
import { usePrismicStaleTime } from '@/data/prismicHooks';
import { useActiveProductConfig } from '@/hooks/use-active-product';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { LaunchSlideItemDocument, LaunchSlidesDocument } from '@/types/prismic.generated';
import { QueryFunctionContext, useQuery } from '@tanstack/react-query';

/**
 * A single launch carousel slide — the content of a `launch_slide_item` document.
 */
export type LaunchSlide = LaunchSlideItemDocument['data'];

const EMPTY: LaunchSlide[] = [];

type QueryKey = ['launch_slides', string | undefined];

const fetcher = async (context: QueryFunctionContext<QueryKey>) => {
    const [key, launchSlidesUid] = context.queryKey;
    if (!launchSlidesUid) {
        return undefined;
    }

    const playlist = await prismicClient.getByUID<LaunchSlidesDocument>(key, launchSlidesUid, {
        graphQuery: `{
            launch_slides {
                ...launch_slidesFields
                launch_slide_items {
                    item {
                        ...itemFields
                    }
                }
            }
        }`,
    });

    return (playlist.data.launch_slide_items ?? [])
        .map(entry => (isDocumentFilled(entry.item) ? entry.item.data : undefined))
        .filter((slide): slide is LaunchSlide => !!slide);
};

/**
 * Launch carousel slides for the current product.
 *
 * Falls back to the legacy global config `launchpromo` slice when the current product has no
 * launch_slides playlist — the original implementation, still serving shipped binaries.
 */
export const useLaunchSlides = (): { slides: LaunchSlide[]; isLoading: boolean } => {
    const productConfig = useActiveProductConfig();
    const globalConfigPromos = useJurisdictionStore(
        state => state.jurisdictionSettings?.globalSettings?.launchPromo?.[0]?.items
    );
    const launchSlidesUid = productConfig?.launchSlidesUid;
    const staleTime = usePrismicStaleTime();

    const { data: fetchedSlides, isPending } = useQuery({
        queryKey: ['launch_slides', launchSlidesUid],
        queryFn: fetcher,
        staleTime,
        enabled: !!launchSlidesUid,
    });

    const isLoading = !!launchSlidesUid && isPending;

    const slides = useMemo(() => {
        if (isLoading) {
            return EMPTY;
        }

        if (fetchedSlides && fetchedSlides.length > 0) {
            return fetchedSlides;
        }

        // Fallback: the original global config launchpromo slice.
        return (globalConfigPromos as LaunchSlide[] | undefined) ?? EMPTY;
    }, [isLoading, fetchedSlides, globalConfigPromos]);

    return { slides, isLoading };
};
